import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import { useUniversityOptions } from '../../../hooks/useUniversityOptions';
import { useAuth } from '../../../context/authContext';
import { useApplicationFormApi } from './applicationFormApi';
import { WizardValues } from './wizardTypes';
import { isTemporaryNumber, TEMPORARY_LABEL } from '../../../components/temporaryNumber';

interface Step1Props {
  values: WizardValues;
  setField: <K extends keyof WizardValues>(field: K, value: WizardValues[K]) => void;
  studentId: number | null;
  applicationNumber: string | null;
}

export const Step1ApplicationDetails = ({ values, setField, studentId, applicationNumber }: Step1Props) => {
  const { user, hasPermission } = useAuth();
  const api = useApplicationFormApi();
  const { listLookup } = api;
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canPickBranch = hasPermission('branches', 'view');

  const branchesQuery = useQuery({
    queryKey: ['lookup', '/branches'],
    queryFn: () => listLookup('/branches', { activeOnly: true }),
    enabled: canPickBranch,
  });
  // Active universities, plus this record's own saved one if deactivated.
  const universitiesQuery = useUniversityOptions([values.preferredUniversityId]);
  // Student Application correction: Program now depends directly on the
  // selected University (Course.universityId, already a required column —
  // never a second, hardcoded mapping) rather than on an intermediate
  // Faculty selection, which has been removed from this step entirely.
  const coursesQuery = useQuery({
    queryKey: ['lookup', '/master-data/courses', values.preferredUniversityId],
    queryFn: () =>
      listLookup('/master-data/courses', {
        activeOnly: true,
        universityId: values.preferredUniversityId,
      }),
    enabled: values.preferredUniversityId !== '',
  });
  const photosQuery = useQuery({
    queryKey: ['student-documents', studentId, 'Photo'],
    queryFn: () => api.listDocuments(studentId!),
    enabled: !!studentId,
  });
  const photo = photosQuery.data?.find((d) => d.documentType.name === 'Photo');

  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      const documentTypesRes = await listLookup('/master-data/document-types', { activeOnly: true });
      const photoType = documentTypesRes.find((t) => t.name === 'Photo');
      if (!photoType) throw new Error('Photo document type not configured');
      return api.uploadDocument(studentId!, file, photoType.id as number);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['student-documents', studentId] }),
  });

  return (
    <Stack spacing={2}>
      {applicationNumber && (
        <Chip
          label={isTemporaryNumber(applicationNumber) ? `${TEMPORARY_LABEL.application}: ${applicationNumber}` : `Application number: ${applicationNumber}`}
          color="primary"
          variant="outlined"
          sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
        />
      )}

      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar src={undefined} sx={{ width: 64, height: 64 }}>
          {values.firstName?.[0] ?? '?'}
        </Avatar>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadPhotoMutation.mutate(file);
            e.target.value = '';
          }}
        />
        <Tooltip title={studentId ? 'Upload student photo' : 'Complete Step 2 first to upload a photo'}>
          <span>
            <IconButton disabled={!studentId} onClick={() => fileInputRef.current?.click()}>
              <UploadFileIcon />
            </IconButton>
          </span>
        </Tooltip>
        {photo && <Typography variant="body2">{photo.originalName}</Typography>}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {/* The counsellor and branch are IMC's to set — not shown in the student's own form. */}
        {api.mode === 'staff' && <TextField label="Counselor" fullWidth disabled value={user ? `${user.firstName} ${user.lastName}` : ''} />}
        {canPickBranch && (
          <TextField
            select
            label="Branch"
            fullWidth
            value={values.branchId}
            onChange={(e) => setField('branchId', e.target.value ? Number(e.target.value) : '')}
          >
            <MenuItem value="">
              <em>Default (my branch)</em>
            </MenuItem>
            {branchesQuery.data?.map((b) => (
              <MenuItem key={b.id as number} value={b.id as number}>
                {String(b.name)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <Typography variant="subtitle2" sx={{ pt: 1 }}>
        Course applying for
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="University"
          fullWidth
          value={values.preferredUniversityId}
          onChange={(e) => {
            // A University change invalidates whatever Program was picked
            // under the previous one — never leave a stale, now-mismatched
            // selection (also enforced server-side, see students.service.ts).
            setField('preferredUniversityId', e.target.value ? Number(e.target.value) : '');
            setField('preferredProgramId', '');
          }}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {universitiesQuery.data?.map((u) => (
            <MenuItem key={u.id as number} value={u.id as number}>
              {String(u.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Semester / Intake"
          placeholder="e.g. January 2027"
          fullWidth
          value={values.preferredIntakeLabel}
          onChange={(e) => setField('preferredIntakeLabel', e.target.value)}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          label="Program"
          fullWidth
          disabled={values.preferredUniversityId === '' || coursesQuery.isFetching || coursesQuery.data?.length === 0}
          value={values.preferredProgramId}
          helperText={
            values.preferredUniversityId === ''
              ? 'Select university first'
              : coursesQuery.isFetching
                ? 'Loading programs…'
                : coursesQuery.data?.length === 0
                  ? 'No programs available for this university'
                  : ' '
          }
          onChange={(e) => setField('preferredProgramId', e.target.value ? Number(e.target.value) : '')}
        >
          <MenuItem value="">
            <em>Not specified</em>
          </MenuItem>
          {coursesQuery.data?.map((c) => (
            <MenuItem key={c.id as number} value={c.id as number}>
              {String(c.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Academic Year"
          placeholder="e.g. 2026/2027"
          fullWidth
          value={values.academicYear}
          onChange={(e) => setField('academicYear', e.target.value)}
        />
      </Stack>
    </Stack>
  );
};
